import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X, Check } from "lucide-react";
import { COUNTRIES, flagEmoji, findByIso } from "../lib/countries";

// CountryPicker — flag + dial-code selector with a searchable modal.
// value: ISO alpha-2 (e.g. "IN"); onChange(iso).

export default function CountryPicker({ value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);

  const selected = findByIso(value) || findByIso("IN");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(([iso, name, dial]) =>
      name.toLowerCase().includes(q) ||
      iso.toLowerCase() === q ||
      iso.toLowerCase().startsWith(q) ||
      dial.replace("+", "").startsWith(q.replace("+", "")) ||
      name.toLowerCase().replace(/\s/g, "").includes(q.replace(/\s/g, ""))
    );
  }, [query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setTimeout(() => searchRef.current?.focus(), 60);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        data-testid="country-picker-button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="flex items-center gap-1.5 pl-3 pr-2 py-3 rounded-l-lg border border-r-0 border-white/10 bg-white/[0.02] hover:bg-white/[0.05] transition-colors text-sm text-white shrink-0 disabled:opacity-50"
      >
        <span className="text-lg leading-none">{selected.flag}</span>
        <span className="tabular text-gray-300">{selected.dial}</span>
        <ChevronDown size={12} className="text-gray-500" />
      </button>

      {open && (
        <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="country-picker-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative card-dark w-full sm:max-w-md max-h-[85vh] sm:max-h-[70vh] border border-white/10 rounded-t-2xl sm:rounded-xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 shrink-0">
              <h3 className="text-white font-medium">Select country</h3>
              <button onClick={() => setOpen(false)} data-testid="country-picker-close" className="text-gray-400 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="px-5 py-3 border-b border-white/10 shrink-0">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  ref={searchRef}
                  data-testid="country-picker-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search country, name or code"
                  className="input-dark !pl-9 !py-2.5 w-full text-sm"
                />
              </div>
            </div>
            <div className="overflow-y-auto flex-1" data-testid="country-picker-list">
              {filtered.length === 0 && (
                <p className="text-center text-gray-500 text-sm py-10">No countries match "{query}"</p>
              )}
              {filtered.map(([iso, name, dial]) => {
                const active = iso === selected.iso;
                return (
                  <button
                    key={iso + dial}
                    type="button"
                    data-testid={`country-option-${iso}`}
                    onClick={() => { onChange(iso); setOpen(false); }}
                    className={`w-full flex items-center gap-3 px-5 py-2.5 text-left transition-colors ${
                      active ? "bg-[#7000FF]/15" : "hover:bg-white/[0.04]"
                    }`}
                  >
                    <span className="text-xl leading-none w-7 text-center">{flagEmoji(iso)}</span>
                    <span className={`flex-1 text-sm ${active ? "text-white" : "text-gray-300"}`}>{name}</span>
                    <span className="tabular text-sm text-gray-500">{dial}</span>
                    {active && <Check size={14} className="text-[#7000FF]" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
